import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, Settings2, Sparkles } from 'lucide-angular';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ReductionActionsService } from 'src/app/shared/services/reduction-actions.service';
import {
  ReductionActionCode,
  ReductionActionDef,
  TransportAlternativeOptions,
} from 'src/app/shared/model/reduction-action';
import { TransportAlternativeOptionsComponent } from './transport-alternative-options/transport-alternative-options.component';

/**
 * Reduction actions the company commits to — these are reduction tips, and the
 * selected ones are highlighted on every trip report. Kept separate from custom
 * actions and SDGs, which are declarative only.
 */
@Component({
  selector: 'app-reduction-actions',
  standalone: true,
  imports: [
    FormsModule,
    TranslocoModule,
    CheckboxModule,
    LucideAngularModule,
    TransportAlternativeOptionsComponent,
  ],
  templateUrl: './reduction-actions.component.html',
})
export class ReductionActionsComponent implements OnInit {
  private reductionActionsService = inject(ReductionActionsService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);

  readonly Sparkles = Sparkles;
  readonly Settings2 = Settings2;

  catalogue = signal<ReductionActionDef[]>([]);
  isSaving = signal(false);

  /** Null until loaded, or when the company's options could not be read. */
  transportAlternativeOptions = signal<TransportAlternativeOptions | null>(
    null,
  );
  showTransportAlternativeOptions = signal(false);

  selectedActions = this.reductionActionsService.reductionActions;
  selectedCount = computed(() => this.selectedActions().length);

  ngOnInit(): void {
    this.reductionActionsService.getCatalogue().subscribe({
      next: (catalogue) => this.catalogue.set(catalogue),
      error: () => this.catalogue.set([]),
    });
    this.reductionActionsService.getTransportAlternativeOptions().subscribe({
      next: (options) => this.transportAlternativeOptions.set(options),
      error: () => this.transportAlternativeOptions.set(null),
    });
  }

  /** The catalogue's thresholds, with the company's own flight distance once known. */
  thresholdsFor(action: ReductionActionDef): Record<string, number> {
    const distance = this.companyFlightDistance(action);
    if (distance === undefined || distance === null) return action.thresholds;
    return { ...action.thresholds, maxDistanceKm: distance };
  }

  /** A company that compares every flight has no distance to quote. */
  descriptionKey(action: ReductionActionDef): string {
    const base = `objectives.reductionActions.${action.code}`;
    return this.companyFlightDistance(action) === null
      ? `${base}.descriptionAllFlights`
      : `${base}.description`;
  }

  /** Undefined when it does not apply or is not loaded yet; null for every flight. */
  private companyFlightDistance(
    action: ReductionActionDef,
  ): number | null | undefined {
    if (action.code !== 'transport_alternative') return undefined;
    return this.transportAlternativeOptions()?.maxFlightDistanceKm;
  }

  isSelected(code: ReductionActionCode): boolean {
    return this.selectedActions().includes(code);
  }

  toggle(code: ReductionActionCode): void {
    if (this.isSaving()) return;

    const current = this.selectedActions();
    const next = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];

    this.isSaving.set(true);
    this.reductionActionsService.updateReductionActions(next).subscribe({
      next: () => this.isSaving.set(false),
      error: () => {
        this.isSaving.set(false);
        this.notificationService.error(
          this.translocoService.translate('objectives.sdgSaveError'),
        );
      },
    });
  }
}
